import { Dayjs } from "dayjs";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { DatePickerView } from "../../../../enums";
import DateType, { getUtcDayjs } from "../../../../types/Date";
import { getValidDayjsDate, isMaxDay, isMinDay } from "../utils/DateUtils";
import { IDateSingleValuePopupSharedProps } from "./Calendar.utils";
import { DatePickerPopup } from "./DatePickerPopup";

interface IState {
    previewValue: Dayjs;
    view: DatePickerView;
}

export interface IDateTimePickerPopup extends IDateSingleValuePopupSharedProps, WithTranslation {
    previewValue?: Date;
}

class DateTimePickerPopup extends React.PureComponent<IDateTimePickerPopup, IState> {
    static defaultProps: Partial<IDateTimePickerPopup> = {
        disableAutoFocus: true
    };

    _calendarRef = React.createRef<React.ElementType>();

    constructor(props: IDateTimePickerPopup) {
        super(props);

        const isSpecialDay = isMinDay(props.value) || isMaxDay(props.value);

        this.state = {
            previewValue: getValidDayjsDate(isSpecialDay ? null : this.props.value ?? this.props.previewValue),
            view: DatePickerView.Days
        };
    }

    componentDidMount(): void {
        if (!this.props.disableAutoFocus) {
            setTimeout(() => this.focusOnCalendar(), 0);
        }
    }

    componentDidUpdate(prevProps: Readonly<IDateTimePickerPopup>) {
        if (this.props.value !== prevProps.value && DateType.isValid(this.props.value)) {
            this.setState({ previewValue: getValidDayjsDate(this.props.value) });
        }
    }

    focusOnCalendar = () => {
        // focus on day only after the popup opens (not when switching between date/time using tabs)
        (this._calendarRef?.current as any)?.focus();
    };

    handleViewChange = (view: DatePickerView) => {
        this.setState({
            view: view
        }, this.focusOnCalendar);
    };

    handleDateChange = (value: Date) => {
        this.setState(() => {
            return {
                previewValue: !value ? null : getUtcDayjs(value)
            };
        });
    };

    handleOk = () => {
        this.props.onChange(!this.state.previewValue ? null : this.state.previewValue.toDate());
    };

    handleCancel = () => {
        this.props.onChange(this.props.value);
    };

    render = () => {
        return (
            <DatePickerPopup value={this.state.previewValue?.toDate()}
                             onChange={this.handleDateChange}
                             onViewChange={this.handleViewChange}
                             onKeyDown={this.props.onKeyDown}
                             showSpecialValue={this.props.showSpecialValue}
                             calendarRef={this._calendarRef}
                             showTime
                             showConfirmButtons
                             onOk={this.handleOk}
                             onCancel={this.handleCancel}
                             disableAutoFocus/>
        );
    };
}

const DateTimePickerPopupWithTranslation = withTranslation("Components")(DateTimePickerPopup);

export { DateTimePickerPopupWithTranslation as DateTimePickerPopup };